import React, { useCallback, useState } from "react"

import { COLOR_PALETTE } from "@/constants/color-palette"
import { Popover, PopoverContent, PopoverTrigger } from "@heroui/react"

interface ColorGridPickerProps {
  /** Current hex color value (e.g., "#1B9E77") */
  value: string
  /** Callback fired when user selects a new color */
  onChange: (color: string) => void
  /** Accessible label for the color picker button */
  label?: string
}

/**
 * Color picker component with a grid of colorblind-friendly swatches
 *
 * Displays a popover with 50 carefully-selected colors organized in a 10×5
 * grid. The palette is based on Okabe-Ito and Paul Tol color schemes,
 * ensuring accessibility for users with color vision deficiencies.
 */
export const ColorGridPicker: React.FC<ColorGridPickerProps> = ({
  value,
  onChange,
  label,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [hexInput, setHexInput] = useState(
    value.replace(/^#/, "").toUpperCase(),
  )

  // Sync local input when the popover opens or the parent value changes while closed
  const prevValueRef = React.useRef(value)
  React.useEffect(() => {
    if (prevValueRef.current !== value) {
      prevValueRef.current = value
      setHexInput(value.replace(/^#/, "").toUpperCase())
    }
  }, [value])

  const handleColorSelect = useCallback(
    (color: string) => {
      onChange(color)
      setHexInput(color.replace(/^#/, "").toUpperCase())
      setIsOpen(false)
    },
    [onChange],
  )

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      offset={8}
    >
      <PopoverTrigger>
        <button
          type="button"
          className="w-10 h-10 rounded-md border-2 border-coval-border
            cursor-pointer shrink-0 transition-all hover:scale-105
            hover:border-coval-border-active focus:outline-none focus:ring-2
            focus:ring-coval-info-border focus:ring-offset-2"
          style={{ backgroundColor: value }}
          title={label || "Select color"}
          aria-label={label || "Select color"}
        />
      </PopoverTrigger>
      <PopoverContent className="p-3">
        <div className="flex flex-col gap-2">
          <div className="text-xs font-medium text-coval-text-muted mb-1">
            Choose a color
          </div>
          <div
            className="grid gap-1.5"
            style={{
              gridTemplateColumns: "repeat(10, 1fr)",
              width: "280px",
            }}
          >
            {COLOR_PALETTE.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => handleColorSelect(color)}
                className={`
                  w-6 h-6 rounded cursor-pointer transition-all
                  hover:scale-110 hover:shadow-md
                  focus:outline-none focus:ring-2 focus:ring-coval-info-border
                  ${value.toLowerCase() === color.toLowerCase() ? "ring-2 ring-coval-info-border scale-110 shadow-md" : ""}
                `}
                style={{ backgroundColor: color }}
                title={color}
                aria-label={`Select color ${color}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5 pt-1">
            <span className="text-xs text-coval-text-muted">#</span>
            <input
              type="text"
              className="flex-1 text-xs font-mono bg-surface-1 border border-coval-border rounded px-1.5 py-1 text-coval-text focus:outline-none focus:ring-1 focus:ring-coval-info-border"
              value={hexInput}
              onChange={(e) => {
                const raw = e.target.value
                  .replace(/[^0-9a-fA-F]/g, "")
                  .slice(0, 6)
                setHexInput(raw.toUpperCase())
                if (raw.length === 6) {
                  onChange(`#${raw}`)
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && /^[0-9a-fA-F]{6}$/.test(hexInput)) {
                  handleColorSelect(`#${hexInput}`)
                }
              }}
              maxLength={6}
              placeholder="6366F1"
              aria-label="Custom hex color code"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
